import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { keyframes, styled } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';

export const StyledText = styled('text')(({ theme }: { theme: Theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 20,
  fontWeight: 600,
}));

export const EditButton = styled('div')`
  background-color: #00cfff21;
  height: 32px;
  width: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.palette.primary.main};

  &:hover {
    color: ${({ theme }) => theme.palette.primary['04']};
  }
`;

export const CurrentToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary['01'],
    color: theme.palette.white['01'],
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const arrowBounceColor = keyframes`
  0% {
    transform: translateY(-2px);
  }
  50% {
    transform: translateY(2px);
  }
  100% {
    transform: translateY(-2px);
  }
`;

export const AnimatedArrow = styled('div')(({ theme }: { theme: Theme }) => ({
  '& > svg': {
    display: 'block',

    animationName: `${arrowBounceColor}`,
    animationDuration: '1.5s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',

    '@keyframes color-change-down': {
      '0%, 100%': {
        color: theme.palette.primary['01'],
      },
      '50%': {
        color: theme.palette.primary['04'],
      },
    },

    // Aplicando a animação combinada
    animation: `${arrowBounceColor} 1.5s ease-in-out infinite, color-change-down 1.5s ease-in-out infinite`,
    color: theme.palette.primary['01'],
  },
}));

// --- UptimeBoxPosition (Corrigido) ---
export const UptimeBoxPosition = styled(Box)`
  background-color: ${({ theme }) => theme.palette.primary['02']}80;
  border-radius: 50%;
  padding: 12px;
  width: 48px;
  height: 48px;
`;

export const UptimeTypography = styled(Typography)`
  font-weight: 400;
  font-size: 20px;
  width: 100%;
  text-align: left;
  font-family: 'Orbitron';
  white-space: pre-line;
  color: ${({ theme }) => theme.palette.primary['01']};
`;

export const UptimeClock = styled('div')`
  /* Ensures the element takes up space correctly */
  display: block;
  width: 100%;

  /* Font settings */
  font-family: 'Orbitron', sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 1.5;
  margin-top: 8px;
  text-align: left;

  font-variant-numeric: tabular-nums;
  font-synthesis: none;

  color: ${({ theme }) => theme.palette.primary['01']};

  animation: blink 1s steps(1, start) infinite;

  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`;

export const ChartsGrid = styled('div')`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  margin-top: 12px;

  /* Stacks both gauges vertically so each one keeps its full size */
  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }
`;

export const UptimeBox = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 28px;
  padding-left: 28px;
  height: 100%;
  width: 100%;
  justify-content: flex-start;
`;

export const LanGrid = styled('div')`
  display: flex;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
  height: auto;
  width: 100%;
  align-items: start;
  grid-auto-rows: auto;
  grid-auto-flow: row;

  @media (max-width: 1400px) {
    grid-template-columns: 1.2fr 1.8fr;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 20px;
    min-height: auto;
  }
`;

export const LanPortBox = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isPon',
})<{ isActive?: boolean; isPon?: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: row;
  height: auto;
  padding: 8px;
  border-radius: 8px;
  border: 2px solid
    ${({ theme, isActive, isPon }) =>
      isActive
        ? isPon
          ? theme.palette.secondary.main
          : theme.palette.primary.main
        : 'transparent'};
  background-color: ${({ theme, isActive, isPon }) =>
    isActive
      ? isPon
        ? theme.palette.secondary['01']
        : theme.palette.primary['01']
      : theme.palette.grey['02']};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  color: ${({ theme }) =>
    theme.palette.mode === 'light'
      ? theme.palette.fixed.white
      : theme.palette.grey['04']};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.primary.main};
  }
`;

export const ConnectionDetailsStack = styled(Stack)({
  flexDirection: 'column',
  gap: 2,
  maxHeight: '400px',
  width: '100%',
});

export const LoadingContainer = styled(Stack)`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 200px;

  & .MuiCircularProgress-root {
    color: ${({ theme }) =>
      theme.palette.white?.['06'] ?? theme.palette.text.secondary};
  }
`;

export const LoadingText = styled(Typography)`
  color: ${({ theme }) =>
    theme.palette.white?.['06'] ?? theme.palette.text.secondary};
`;

export const ChartColumn = styled(Stack)`
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

export const UptimeInfoBadge = styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 32px;
  gap: 4px;
  font-size: 12px;
  align-items: center;
`;

export const UptimeBadgeTitle = styled(Typography)`
  width: fit-content;
  font-weight: 600;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.palette.white['06']};
`;

export const WanStatsTitle = styled(Typography)`
  text-align: center;
`;
